import axios, { type InternalAxiosRequestConfig } from 'axios'
import { reportUnauthorized } from 'shared/auth/sessionExpiry'
import { CSRF_HEADER_NAME, ensureFreshCsrfToken, requiresCsrfToken } from './csrf'
import { DEFAULT_LOCALE, i18next, isLocale } from 'shared/i18n'

const apiClient = axios.create({
  baseURL: process.env.JIZAIPAD_API_URL,
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    Accept: 'application/json'
  }
})

// CUD の直前に CSRF トークンを取り直す。SPA では初回ロード以降 /v1/sessions が呼ばれず、
// Cookie を失った状態のまま CSRF エラー(API 側 500)になり続けるため、送信ごとに再発行させる。
// ensureFreshCsrfToken() は fetch で取得するので apiClient を再帰的に呼ばない。
apiClient.interceptors.request.use(async (config) => {
  // API は Accept-Language で返す文言の言語を決める（jizaipad-api #4198）。ブラウザが自動で付けるのは
  // OS の言語で、画面で選んだ言語と一致しないため、表示中の言語を明示して送る。
  config.headers.set('Accept-Language', isLocale(i18next.language) ? i18next.language : DEFAULT_LOCALE)
  config.xsrfCookieName = 'CSRF-TOKEN'
  config.xsrfHeaderName = CSRF_HEADER_NAME

  if (requiresCsrfToken(config.method)) {
    try {
      await ensureFreshCsrfToken()
    } catch {
      // 取得に失敗しても本リクエストは止めない。既存 Cookie で通る可能性があり、
      // 通らなければ本リクエスト自身のエラーとして通常の経路に乗せる。
    }
  }
  return config
})

// API セッションには refresh の仕組みが無いため、401 は再試行しない。
// セッション切れかどうかの確認とログイン画面への誘導は sessionExpiry に委ね、
// エラー自体はそのまま呼び出し側へ返す(業務エラーとしての 401 もあるため握りつぶさない)。
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      reportUnauthorized((error.config as InternalAxiosRequestConfig | undefined)?.url)
    }
    return await Promise.reject(error)
  }
)

export default apiClient
